/** 自動保存協調器對外的狀態（spec 6.2）。 */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'quota-exceeded';

export type SaveTone = 'muted' | 'accent' | 'success' | 'danger' | 'warning';

export interface SaveStatusDescription {
  label: string;
  /** 狀態不只以顏色表示，另有文字與圖示（spec 25.1）。 */
  tone: SaveTone;
  icon: string;
  retryable: boolean;
  detail: string;
}

const DESCRIPTIONS: Record<SaveState, SaveStatusDescription> = {
  idle: {
    label: '已儲存',
    tone: 'muted',
    icon: 'CloudCheck',
    retryable: false,
    detail: '所有變更都保存在這個瀏覽器裡。',
  },
  saving: {
    label: '儲存中',
    tone: 'accent',
    icon: 'CloudArrowUp',
    retryable: false,
    detail: '正在把剛才的變更寫入。',
  },
  saved: {
    label: '已儲存',
    tone: 'success',
    icon: 'CloudCheck',
    retryable: false,
    detail: '所有變更都保存在這個瀏覽器裡。',
  },
  error: {
    label: '儲存失敗',
    tone: 'danger',
    icon: 'CloudWarning',
    retryable: true,
    detail: '畫面上的內容還在，可以再試一次。',
  },
  'quota-exceeded': {
    label: '空間不足',
    tone: 'warning',
    icon: 'HardDrives',
    retryable: true,
    detail: '瀏覽器儲存空間快滿了，可以到備份頁刪除不需要的圖片。',
  },
};

export function describeSaveStatus(state: SaveState): SaveStatusDescription {
  return DESCRIPTIONS[state];
}

export interface SavedAtDescription {
  /** 狀態列上直接顯示的時鐘字串，例如 14:30:05。 */
  clock: string;
  /** tooltip 用的完整時間，例如 2026/08/24 14:30:05。 */
  full: string;
  /** 「剛剛」「3 分鐘前」這種相對說法，讓人一眼分辨是不是剛存的。 */
  relative: string;
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * 把最後一次成功寫入的時間講清楚（spec 6.2）。
 *
 * 只寫「已儲存」看不出是這一秒存的還是十分鐘前存的，所以狀態列一律附上時間。
 * 顯示用本地時間，因為使用者比對的是自己牆上的鐘。
 */
export function describeSavedAt(
  savedAt: string | null | undefined,
  now: Date = new Date(),
): SavedAtDescription | null {
  if (!savedAt) return null;

  const at = new Date(savedAt);
  if (Number.isNaN(at.getTime())) return null;

  const clock = `${pad2(at.getHours())}:${pad2(at.getMinutes())}:${pad2(at.getSeconds())}`;
  const full = `${at.getFullYear()}/${pad2(at.getMonth() + 1)}/${pad2(at.getDate())} ${clock}`;

  // 時鐘可能被往回調，負秒數一律當成剛剛，不要出現「-3 分鐘前」。
  const seconds = Math.max(0, Math.floor((now.getTime() - at.getTime()) / 1000));
  const relative =
    seconds < 10
      ? '剛剛'
      : seconds < 60
        ? `${seconds} 秒前`
        : seconds < 3600
          ? `${Math.floor(seconds / 60)} 分鐘前`
          : seconds < 86_400
            ? `${Math.floor(seconds / 3600)} 小時前`
            : `${Math.floor(seconds / 86_400)} 天前`;

  return { clock, full, relative };
}
